import React, { useState } from 'react';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/plus.svg';
import {
  Button,
  Flex,
  FlexColumn,
  CodeEditor,
  FormControls,
  InfoTooltip,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import useSelectedWarehouseType from 'shared/hooks/useSelectedWarehouseType';

type Props = {
  value?: string;
  onChange?: (val: string) => void;
};

const WhereClauseControl = React.forwardRef(
  ({ value, onChange }: Props, ref: any) => {
    const warehouseType = useSelectedWarehouseType();
    const [isEditing, setIsEditing] = useState(false);
    const hasValue = !!value?.trim();
    const mode = isEditing ? 'edit' : hasValue ? 'done' : 'initial';

    return (
      <Flex
        ref={ref}
        sx={{
          gap: 2,
          alignItems: mode === 'initial' ? 'center' : 'flex-start',
        }}
      >
        {mode === 'initial' && (
          <Flex sx={{ gap: 2, alignItems: 'center' }}>
            <Button
              sx={{ width: 'fit-content' }}
              variant="outlined"
              startIcon={<PlusIcon />}
              onClick={() => setIsEditing(true)}
            >
              Add a ‘where’ clause
            </Button>
            <InfoTooltip title="Add a where clause to limit the data scanned (e.g. status='completed') or filter the specific data you want to test (e.g. excluding test data)" />
          </Flex>
        )}

        {mode === 'edit' && (
          <FormControls.FieldsWrapper sx={{ width: '100%' }}>
            <FlexColumn sx={{ width: '100%', gap: 2 }}>
              <Typography>Add a ‘where’ clause:</Typography>

              <Flex sx={{ gap: 2, alignItems: 'flex-start', width: '100%' }}>
                <CodeEditor
                  height="80px"
                  mode="sql"
                  value={value}
                  onChange={(val: string) => onChange?.(val)}
                />

                <InfoTooltip title="Add a where clause to limit the data scanned (e.g. status='completed') or filter the specific data you want to test (e.g. excluding test data)" />
              </Flex>

              <Flex sx={{ gap: 1 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    onChange?.('');
                    setIsEditing(false);
                  }}
                >
                  Discard
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setIsEditing(false)}
                  disabled={!value || !value.trim()}
                >
                  Save
                </Button>
              </Flex>
            </FlexColumn>
          </FormControls.FieldsWrapper>
        )}

        {mode === 'done' && (
          <FormControls.FieldsWrapper sx={{ width: '100%' }}>
            <FlexColumn sx={{ width: '100%', gap: 2 }}>
              <Typography>‘where’ clause:</Typography>
              <Flex sx={{ gap: 2, alignItems: 'flex-start', width: '100%' }}>
                <CodeEditor
                  height="80px"
                  mode="sql"
                  warehouseType={warehouseType}
                  value={value}
                  readOnly
                />
                <InfoTooltip title="Add a where clause to limit the data scanned (e.g. status='completed') or filter the specific data you want to test (e.g. excluding test data)" />
              </Flex>

              <Flex sx={{ gap: 1 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    onChange?.('');
                    setIsEditing(false);
                  }}
                >
                  Delete
                </Button>
                <Button variant="outlined" onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              </Flex>
            </FlexColumn>
          </FormControls.FieldsWrapper>
        )}
      </Flex>
    );
  },
);

export default WhereClauseControl;
